import React, { useState, useEffect, useContext } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "../components/ui/card";
import { toast } from "../components/ui/use-toast";
import axios from "axios";
import { Loader2 } from "lucide-react";

export default function RegisterMicrosoftPage() {
  const {
    login,
    isPendingMicrosoftRegistration,
    pendingMicrosoftUserDetails,
    clearPendingRegistration,
    navigatorRole,
  } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    role: "None", 
    phone: "",
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // If not pending registration or details are missing, redirect to login
    if (!isPendingMicrosoftRegistration || !pendingMicrosoftUserDetails) {
      console.log("Not pending MS registration, redirecting to login.");
      clearPendingRegistration(); // Clear any potentially stale state
      navigate("/login", { replace: true });
    } else {
      // Pre-fill form with details from Microsoft
      setForm((f) => ({
        ...f,
        full_name: pendingMicrosoftUserDetails.name || "",
        email: pendingMicrosoftUserDetails.email || "",
        // If we have a navigator role, use it, otherwise default to None
        role: navigatorRole || "None",
      }));
      
      // Log the role detection
      console.log("Setting form role based on navigator role:", navigatorRole);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isPendingMicrosoftRegistration,
    pendingMicrosoftUserDetails,
    navigate,
    clearPendingRegistration,
    navigatorRole,
  ]); // Don't add setForm to deps

  // Add a new useEffect to fetch navigator permissions when Microsoft details are available
  useEffect(() => {
    async function fetchNavigatorRole() {
      if (pendingMicrosoftUserDetails?.onPremisesSamAccountName) {
        try {
          console.log("Fetching navigator role for:", pendingMicrosoftUserDetails.onPremisesSamAccountName);
          
          const response = await axios.post("/employee/get_navigator_permissions", {
            user_id: pendingMicrosoftUserDetails.onPremisesSamAccountName
          });
          
          console.log("Navigator permissions response:", response.data);
          
          if (response.data && response.data.org_role) {
            // Update form with the received role
            setForm(prevForm => ({
              ...prevForm,
              role: response.data.org_role
            }));
            
            console.log("Navigator role set to:", response.data.org_role);
          } else if (response.data && response.data.status === "error") {
            console.error("Navigator API error:", response.data.message);
          } else {
            console.log("No org_role in response");
          }
        } catch (error) {
          console.error("Error fetching navigator permissions:", error);
          toast({
            title: "Role Detection Failed",
            description: "Could not connect to role service. Default role will be used.",
            variant: "destructive"
          });
        }
      }
    }
    
    fetchNavigatorRole();
  }, [pendingMicrosoftUserDetails]); // Only run when Microsoft details change

  const handleChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  async function handleSubmit(e) {
    e.preventDefault();
    if (!pendingMicrosoftUserDetails?.oid) {
      toast({
        title: "Registration Error",
        description: "Microsoft user identifier is missing.",
        variant: "destructive",
      });
      return;
    }

    // Role validation is no longer needed since role is always set

    setLoading(true);
    try {
      console.log("Submitting Microsoft registration completion:", {
        ...form,
        oid: pendingMicrosoftUserDetails.oid,
      });
      // Send completed registration data to the new backend endpoint
      const response = await axios.post(
        "/auth/microsoft/complete-registration",
        {
          oid: pendingMicrosoftUserDetails.oid, // Send the Microsoft OID
          full_name: form.full_name,
          email: form.email, // Send email even if read-only, backend might use it
          role: form.role,
          phone: form.phone || null, // Send null if empty
        },
      );

      const appToken = response.data.access_token;
      if (appToken) {
        await login({ token: appToken }); // Use the login function from context
        toast({
          title: "Registration Complete!",
          description: "You are now logged in.",
        });
        navigate("/"); // Navigate to home page after successful registration and login
      } else {
        throw new Error("No access token received from backend.");
      }
    } catch (err) {
      console.error("Microsoft Registration completion failed:", err);
      toast({
        title: "Registration Failed",
        description:
          err.response?.data?.error ||
          err.message ||
          "Could not complete registration.",
        variant: "destructive",
      });
      // Consider if logout() should be called here
    } finally {
      setLoading(false);
    }
  }

  // Render null or a loading indicator if details are not yet loaded
  if (!isPendingMicrosoftRegistration || !pendingMicrosoftUserDetails) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/30">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-muted/30 p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Complete Registration</CardTitle>
          <CardDescription>
            Please confirm your details and select your role to finish setting
            up your account.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form className="space-y-4" onSubmit={handleSubmit}>
            {/* OID is not shown to the user but is required for submission */}
            <Input
              name="email"
              type="email"
              readOnly // Email from Microsoft should not be changed
              placeholder="Email"
              value={form.email}
              className="bg-muted/50 cursor-not-allowed"
              aria-label="Email (from Microsoft)"
            />
            <Input
              name="full_name"
              required
              placeholder="Full Name"
              value={form.full_name}
              onChange={handleChange}
              aria-label="Full Name"
            />
            <Input
              name="phone"
              placeholder="Phone (Optional)"
              value={form.phone}
              onChange={handleChange}
              aria-label="Phone Number"
            />
            <div>
              <label
                htmlFor="role-display"
                className="block text-sm font-medium mb-1"
              >
                Your Role {navigatorRole}
                {!navigatorRole}
              </label>
              <Input
                id="role-display"
                name="role"
                readOnly
                value={form.role === "BM" ? "Business Manager (BM)" : form.role === "RSM" ? "RSM (Field Rep)" : form.role}
                className="bg-muted/50 cursor-not-allowed"
                aria-label="Role"
              />
              {!navigatorRole && 
                <p className="text-xs text-muted-foreground mt-1">
                  Using organization-level role assignment.
                </p>
              }
            </div>

            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Completing Registration...
                </>
              ) : (
                "Complete Registration"
              )}
            </Button>
          </form>
          <div className="mt-6 text-center">
            <Link
              className="text-sm text-muted-foreground hover:text-primary hover:underline"
              to="/login"
              onClick={() => {
                // Clear pending state if they explicitly go back to login
                clearPendingRegistration();
              }}
            >
              Cancel and go back to Login
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
